<template>
  <el-dialog
    :model-value="visible"
    :title="translate('Quantity Tiers')"
    width="960px"
    append-to-body
    :close-on-click-modal="false"
    @update:model-value="$emit('update:visible', $event)"
    @open="load"
    @closed="unload"
  >
    <div v-if="pricesVisible" class="qt-base">
      <span>{{ translate(coefficientMeta.text) }}: <b>{{ component.companyCoeficient }}</b></span>
      <span>{{ translate('Base') }} · {{ translate(priceMeta.text) }}: <b>{{ formatPrice(basePrice) }}</b></span>
    </div>
    <p v-if="pricesVisible && hasChildren" class="qt-hint">
      {{ translate('Sub-components are priced at their base values') }}
    </p>
    <p v-if="panels.length === 0" class="qt-hint">
      {{ translate('No quantity tiers defined') }}
    </p>

    <div v-for="(panel, panelIndex) in panels" :key="panel.key" class="qt-panel">
      <div class="qt-panel-header">
        <label>{{ translate('Quantity from') }}</label>
        <el-input-number
          v-model="panel.threshold"
          :min="1"
          :max="maxThreshold"
          :step="1"
          :precision="0"
          :disabled="structureLocked"
          controls-position="right"
          @change="onThresholdChange(panelIndex)"
        />
        <template v-if="pricesVisible">
          <label>{{ translate(coefficientMeta.text) }}</label>
          <div class="qt-coefficient">
            <cc-var-control
              :metadata="coefficientMeta"
              :entity="panel"
              property="companyCoeficient"
              :rootEntity="component"
              :readOnly="readOnly"
            />
          </div>
          <span class="qt-preview">
            {{ translate(priceMeta.text) }}: <b>{{ formatPrice(panelPrice(panel)) }}</b>
          </span>
        </template>
        <el-button
          v-if="!structureLocked"
          class="qt-remove"
          type="danger"
          plain
          circle
          size="small"
          :title="translate('Remove tier')"
          @click="removePanel(panelIndex)"
        >
          <el-icon><Delete /></el-icon>
        </el-button>
      </div>

      <table class="qt-actions">
        <thead>
          <tr>
            <th>#</th>
            <th>{{ translate(actionTypeMeta.text) }}</th>
            <th>{{ translate('Base') }}</th>
            <th>{{ translate(durationMeta.text) }}</th>
            <template v-if="realDurationMeta">
              <th>{{ translate('Base') }}</th>
              <th>{{ translate(realDurationMeta.text) }}</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in sortedRows(panel)" :key="row.actionIndex">
            <td>{{ actionOf(row).idx }}</td>
            <td>{{ actionTypeText(actionOf(row)) }}</td>
            <td class="qt-base-value">{{ toDurationInUserUnits(actionOf(row).durationInMinutes) }}</td>
            <td>
              <cc-var-control
                :metadata="durationMeta"
                :entity="row"
                property="durationInMinutes"
                :rootEntity="component"
                :readOnly="readOnly"
              />
            </td>
            <template v-if="realDurationMeta">
              <td class="qt-base-value">{{ toDurationInUserUnits(actionOf(row).realDurationInMinutes) }}</td>
              <td>
                <cc-var-control
                  :metadata="realDurationMeta"
                  :entity="row"
                  property="realDurationInMinutes"
                  :rootEntity="component"
                  :readOnly="readOnly"
                />
              </td>
            </template>
          </tr>
        </tbody>
      </table>

      <div v-for="(error, errorIndex) in errorsFor(panelIndex)" :key="errorIndex" class="qt-error">
        {{ translate(error.message) }}
      </div>
    </div>

    <template #footer>
      <el-button
        v-if="!structureLocked"
        class="qt-new"
        :disabled="panels.length >= maxTiers"
        :title="panels.length >= maxTiers ? translate('A product can have at most 10 quantity tiers') : ''"
        @click="addPanel"
      >
        <el-icon><Plus /></el-icon>&nbsp;{{ translate('New tier') }}
      </el-button>
      <el-button @click="close">Cancel</el-button>
      <el-button v-if="!readOnly" type="primary" :disabled="errors.length > 0" @click="apply">OK</el-button>
    </template>
  </el-dialog>
</template>

<script>
import {
  MAX_THRESHOLD,
  MAX_TIERS,
  buildTierPanels,
  addTierPanel,
  setPanelThreshold,
  removeTierPanel,
  validateTierPanels,
  buildTierDocuments,
  basePricePerPiece,
  panelPricePerPiece,
} from '@/utils/quantityTiersEditor.mjs'

export default {
  name: 'CcQuantityTiers',

  emits: ['update:visible', 'applied'],

  props: {
    visible: { type: Boolean, default: false },
    // The component being edited: companyCoeficient, actions[], children[], quantityTiers.
    component: { type: Object, default: null },
    // metadata.item.actions - the actions table metadata whose columns this dialog reuses.
    actionsMetadata: { type: Object, default: null },
    // metadata.item.totals - decides price visibility exactly as the totals block does.
    totalsMetadata: { type: Array, default: null },
    readOnly: { type: Boolean, default: false },
  },

  data() {
    return {
      panels: [],
      maxThreshold: MAX_THRESHOLD,
      maxTiers: MAX_TIERS,
    }
  },

  computed: {
    coefficientMeta() {
      return (this.totalsMetadata || []).find(cell => cell.id === 'companyCoeficient') || null
    },
    // The API sends an empty totals list to users without price access, so the coefficient
    // cell's presence is the same signal the totals block itself renders from. Its own type
    // (decimal or label) makes the coefficient editable or read-only.
    pricesVisible() {
      return !!this.coefficientMeta
    },
    // A tier's threshold decides which coefficient applies at each quantity, so adding, moving
    // or removing a tier is a price change: it needs the same right as editing the coefficient
    // (its cell is `decimal` only for users who may edit prices). Durations stay editable under
    // the component's own edit rights, exactly as in the actions table.
    structureLocked() {
      return this.readOnly || !(this.coefficientMeta && this.coefficientMeta.type === 'decimal')
    },
    priceMeta() {
      return (this.totalsMetadata || []).find(cell => cell.id === '__priceOfWork')
        || { text: 'Price per work (per piece)', suffix: '' }
    },
    columns() {
      return (this.actionsMetadata && this.actionsMetadata.columns) || []
    },
    actionTypeMeta() {
      return this.columns.find(column => column.id === 'actionTypeId') || { text: 'Action Type', options: [] }
    },
    durationMeta() {
      return this.columns.find(column => column.id === 'durationInMinutes')
    },
    // Only where the actions table itself edits the real duration (fabco). Elsewhere the
    // column is hidden and tiers keep the real duration unset.
    realDurationMeta() {
      const column = this.columns.find(c => c.id === 'realDurationInMinutes')
      return column && column.type === 'durationInUserUnits' ? column : null
    },
    hasChildren() {
      return !!(this.component && this.component.children && this.component.children.length)
    },
    basePrice() {
      return this.component ? basePricePerPiece(this.component) : 0
    },
    errors() {
      return validateTierPanels(this.panels)
    },
  },

  methods: {
    // Rebuilt from the component on every open, so Cancel discards everything.
    load() {
      this.panels = this.component ? buildTierPanels(this.component) : []
    },
    // A closed dialog keeps no panels: the product can change underneath it (an operation
    // deleted on the form, another inquiry position selected), and stale rows would index
    // operations that no longer exist. The next open reloads from the product.
    unload() {
      this.panels = []
    },
    addPanel() {
      this.panels = addTierPanel(this.panels, this.component)
    },
    removePanel(panelIndex) {
      this.panels = removeTierPanel(this.panels, panelIndex)
    },
    onThresholdChange(panelIndex) {
      this.panels = setPanelThreshold(this.panels, panelIndex, this.panels[panelIndex].threshold, this.component)
    },
    actionOf(row) {
      return this.component.actions[row.actionIndex]
    },
    sortedRows(panel) {
      return [...panel.rows].sort((a, b) => (this.actionOf(a).idx || 0) - (this.actionOf(b).idx || 0))
    },
    actionTypeText(action) {
      const option = (this.actionTypeMeta.options || []).find(o => String(o.id) === String(action.actionTypeId))
      return option ? this.translate(option.text) : action.actionType
    },
    panelPrice(panel) {
      return panelPricePerPiece(panel, this.component)
    },
    formatPrice(value) {
      return `${value} ${this.priceMeta.suffix || ''}`.trim()
    },
    errorsFor(panelIndex) {
      return this.errors.filter(error => error.panelIndex === panelIndex)
    },
    // Emits the documents rather than writing them into the `component` prop, so the owning
    // form stays the entity's only writer; it applies them with applyTierDocuments.
    apply() {
      if (this.errors.length > 0) return

      this.$emit('applied', buildTierDocuments(this.panels, this.component))
      this.close()
    },
    close() {
      this.$emit('update:visible', false)
    },
  },
}
</script>

<style scoped>
.qt-base {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 8px;
}
.qt-hint {
  opacity: 0.75;
  margin: 4px 0 12px;
}
.qt-panel {
  border: 1px solid var(--el-border-color);
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
}
.qt-panel-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.qt-coefficient {
  width: 140px;
}
.qt-preview {
  margin-left: auto;
}
.qt-actions {
  width: 100%;
  border-collapse: collapse;
}
.qt-actions th,
.qt-actions td {
  padding: 4px 8px;
  text-align: left;
}
.qt-base-value {
  opacity: 0.7;
}
.qt-error {
  color: var(--el-color-danger);
  margin-top: 4px;
}
.qt-new {
  float: left;
}
</style>
